import React, { useState, useCallback, useEffect } from 'react';
import {
    X,
    Search,
    Check,
    Terminal,
    AlertCircle,
    CheckSquare,
    Square,
    Library,
    RefreshCw,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { udfApi } from '../../api/udfApi';

const FunctionLibraryModal = ({ isOpen, onClose, onSelect, currentUdfIds = [] }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [allUdfs, setAllUdfs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedUdfIds, setSelectedUdfIds] = useState(new Set());

    const loadUdfs = useCallback(async () => {
        if (!isOpen) return;
        setIsLoading(true);
        setError(null);
        try {
            const data = await udfApi.listUDFs();
            setAllUdfs(data);
        } catch (err) {
            console.error('Failed to load user UDFs:', err);
            setError('Failed to load functions from your library.');
        } finally {
            setIsLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        loadUdfs();
    }, [loadUdfs]);

    useEffect(() => {
        // Reset selection when modal opens/closes
        if (!isOpen) {
            setSelectedUdfIds(new Set());
        }
    }, [isOpen]);

    const toggleSelection = (udfId) => {
        setSelectedUdfIds(prev => {
            const next = new Set(prev);
            if (next.has(udfId)) {
                next.delete(udfId);
            } else {
                next.add(udfId);
            }
            return next;
        });
    };

    const handleAddSelected = () => {
        const selectedUdfs = allUdfs.filter(u => selectedUdfIds.has(u.id));
        if (selectedUdfs.length > 0) {
            onSelect(selectedUdfs);
            setSelectedUdfIds(new Set());
        }
    };

    if (!isOpen) return null;

    const filteredUdfs = allUdfs.filter(u =>
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.signature.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/20 dark:bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={onClose}
            />

            <div className="relative w-full max-w-xl bg-white dark:bg-gray-950 rounded-[40px] shadow-2xl border border-gray-100 dark:border-gray-800 flex flex-col max-h-[85vh] overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="p-8 border-b border-gray-50 dark:border-gray-900/50 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 rounded-2xl">
                            <Library size={24} />
                        </div>
                        <div>
                            <h3 className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">
                                Function Library
                            </h3>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">
                                Select functions to add to this workspace
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={loadUdfs}
                            disabled={isLoading}
                            className="p-2 text-gray-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
                            title="Refresh Library"
                        >
                            <RefreshCw size={18} className={cn(isLoading && "animate-spin")} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Search */}
                <div className="p-6 border-b border-gray-50 dark:border-gray-900/20 shrink-0">
                    <div className="relative group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-emerald-500 transition-colors" size={16} />
                        <input
                            type="text"
                            placeholder="Search functions..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-4 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-3xl outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 transition-all text-sm font-medium"
                        />
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar min-h-[300px]">
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-2xl flex items-start gap-3">
                            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest">Error</p>
                                <p className="text-xs font-bold text-red-500 dark:text-red-300 mt-1">{error}</p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-1">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-4" />
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Library...</p>
                            </div>
                        ) : filteredUdfs.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 bg-gray-50 dark:bg-gray-900/50 rounded-[40px] border-2 border-dashed border-gray-100 dark:border-gray-800">
                                <Terminal size={40} className="text-gray-200 dark:text-gray-800 mb-4" />
                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No functions found</p>
                            </div>
                        ) : (
                            filteredUdfs.map((udf) => {
                                const isAlreadyAdded = currentUdfIds.includes(udf.id);
                                const isSelected = selectedUdfIds.has(udf.id);
                                return (
                                    <button
                                        key={udf.id}
                                        disabled={isAlreadyAdded}
                                        onClick={() => !isAlreadyAdded && toggleSelection(udf.id)}
                                        className={cn(
                                            "w-full flex items-center gap-4 p-4 rounded-2xl transition-all border group",
                                            isAlreadyAdded
                                                ? "bg-gray-50 dark:bg-gray-900/50 border-gray-100 dark:border-gray-800 opacity-60 cursor-not-allowed"
                                                : isSelected
                                                    ? "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800"
                                                    : "border-transparent hover:bg-emerald-50 dark:hover:bg-emerald-900/10 hover:border-emerald-100 dark:hover:border-emerald-900/50"
                                        )}
                                    >
                                        <div className={cn(
                                            "p-2 rounded-lg transition-all shrink-0",
                                            isAlreadyAdded
                                                ? "bg-gray-200 dark:bg-gray-800 text-gray-400"
                                                : isSelected
                                                    ? "bg-emerald-600 text-white"
                                                    : "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600"
                                        )}>
                                            {isAlreadyAdded ? (
                                                <Check size={16} />
                                            ) : isSelected ? (
                                                <CheckSquare size={16} />
                                            ) : (
                                                <Square size={16} />
                                            )}
                                        </div>
                                        <div className="flex-1 text-left min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-tight truncate">{udf.name}</span>
                                                {isAlreadyAdded && (
                                                    <span className="text-[8px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-md">
                                                        Added
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5 truncate">
                                                {udf.signature}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-50 dark:border-gray-900/50 bg-gray-50/30 dark:bg-gray-900/30 shrink-0">
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                            {selectedUdfIds.size > 0 ? (
                                <span>{selectedUdfIds.size} function{selectedUdfIds.size !== 1 ? 's' : ''} selected</span>
                            ) : (
                                <span>Register more functions in the <span className="text-emerald-600">Function Explorer</span></span>
                            )}
                        </p>
                        {selectedUdfIds.size > 0 && (
                            <button
                                onClick={handleAddSelected}
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-semibold uppercase tracking-wider transition-all"
                            >
                                Add Selected ({selectedUdfIds.size})
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FunctionLibraryModal;
